/**
 * What's New — in-app release-notes data.
 *
 * Each entry describes a single version's user-visible highlights. Keep entries
 * SHORT — 2-5 bullets per release, in plain English, oriented around what the
 * USER can do differently rather than what code shipped. The CHANGELOG.md has
 * the full technical detail; this file is the headline.
 *
 * Maintenance: when bumping the app version, add a new entry to the TOP of
 * WHATS_NEW. The card surfaces all entries newer than the user's last-seen
 * version, so multiple-release jumps show every entry in between (with the
 * latest entry first inside the card).
 *
 * Severity hints (optional per highlight):
 *   - 'feature'  — new capability the user can opt into (default)
 *   - 'fix'      — bug fix; existing behavior now works correctly
 *   - 'breaking' — requires user action (e.g., recompile NT addon)
 *   - 'polish'   — UX/visual improvement, no behavior change
 *
 * The card dot-prefixes each bullet with a colored marker per severity.
 */

export const WHATS_NEW = [
  {
    version: '7.6.2',
    date: '2026-05-28',
    title: 'New app icon · Coach reads your ATM exit style',
    highlights: [
      {
        severity: 'polish',
        text: 'Fresh app icon. Wicks on the candles, a soft teal EMA trail tracing the arc, a faint emerald halo on the breakout, and the constellation tightened up in the corner. Same composition you know — sharpened. Updates everywhere the icon shows up: installer, taskbar, browser favicon, in-app header, PWA install. Heads-up for Windows: if a desktop or Start Menu shortcut still shows the old icon after updating, that\'s the Windows icon cache being sticky — a reboot clears it (or `ie4uinit.exe -show` from PowerShell, if you\'re in a hurry).',
      },
      {
        severity: 'feature',
        text: 'Coach now reads each ATM template\'s exit style (auto-trail / take-profit / hybrid) and adapts what it says. When you switch a template\'s exit type and Coach has enough trades on both sides, it flags the version comparison as a *management-style* change — not a parameter tweak — because the WR delta is between two different strategies, not iterations of the same one. The fix-side language also shades by style: "chase penalty" callouts on a trailing ATM say "use a different, wider-stop ATM for chases"; on a fixed-target ATM they say "the R-target was sized for pullbacks; chases give up runway." Same data, sharper reads.',
      },
      {
        severity: 'feature',
        text: 'Tuning suggestions got a guardrail. Coach won\'t suggest a trail-frequency or trail-stopLoss adjustment on a take-profit template where the field doesn\'t exist. Cross-template patterns ("widening Trail SL has lifted WR on 4 templates") now stay within the templates the pattern actually applies to.',
      },
    ],
  },
  {
    version: '7.6.1',
    date: '2026-05-28',
    title: 'Coach polish · Dashboard restyle · LLM direction-word fix',
    highlights: [
      {
        severity: 'polish',
        text: 'Coach Dashboard restyled to match the v7.6 frosted-glass language — modal frame, grade badge with color-keyed glow, drift cards as tinted glass chips per severity, action buttons on the shared `.btn-glass` family.',
      },
      {
        severity: 'fix',
        text: 'Coach now talks straight about trail-frequency changes. The analyzer was labeling "Trail Frequency 12→24" as "widening" — technically a synonym for looser, but easy for the LLM to flip into "tighter trailing chokes winners" via training-data prior. Analyzer now says "loosening" explicitly; system prompt forbids substituting trader-tropes for the data\'s actual direction words.',
      },
      {
        severity: 'polish',
        text: 'Hottest Scope tile on the Coach Dashboard answers WHY now — "3 edges, 5 leaks here" tells you at a glance whether the scope is bleeding or working. Scan-coverage info moved to the dashboard footer where it belongs.',
      },
    ],
  },
  {
    version: '7.6.0',
    date: '2026-05-27',
    title: 'Frosted-glass UI reset · Coach voice rebalance · faster clicks',
    highlights: [
      {
        severity: 'polish',
        text: 'Every button, tab, dropdown, modal action, and chip across the app now speaks one visual language — translucent color-tinted backgrounds, matching borders, soft hover glows. Same color identity you know (purple for AI, blue for primary, green for CTAs, red for destructive) — flat saturated rectangles are gone. New shared `.btn-glass` CSS family ships alongside so future work inherits the language.',
      },
      {
        severity: 'feature',
        text: 'Coach voice softer without losing teeth. Daily reviews, ATM tuning, version-comparison reports rewritten. Less "you took 3 losing trades — review your discipline." More "today went 3 down — let\'s look at it together." All five personalities re-tuned.',
      },
      {
        severity: 'feature',
        text: 'Accounts management lives on the Accounts tab now — not a modal stacked inside Settings. Fixes a z-index bug where the parent modal\'s footer intercepted clicks on bottom-row Mark Blown / Reset buttons. Adds search, sort (Name / Balance / P&L / Drawdown cushion), and status chip filter (Active / Passed / Blown / Cancelled).',
      },
      {
        severity: 'polish',
        text: 'Every native `<select>` retired. Chromium on Windows ignored `color-scheme: dark` for native popups (Win32 rendering bypasses CSS). New `<GlassSelect>` replaces 36 of them with the frosted aesthetic + auto-search above 8 options, keyboard nav, click-outside, popup-flip-above.',
      },
      {
        severity: 'fix',
        text: 'Click lag killed. The first pass of the restyle pushed Interaction-to-Next-Paint from ~200ms to ~1.3s via three compounding mistakes: per-button `backdrop-blur` stacking on already-blurred parents, `transition-all` animating expensive `backdrop-filter`, and synchronous Dashboard re-renders blocking tab clicks. Stripped redundant blur, switched to specific transitions, wrapped `setActiveTab` in `startTransition`.',
      },
      {
        severity: 'polish',
        text: 'Dropdowns no longer hog horizontal space. Account / Time Period / Sort / Tag filters were stretching edge-to-edge in flex-wrap rows because GlassSelect\'s wrapper defaulted to `w-full`. Sized to content explicitly now; form-grid usages still fill their cells by design.',
      },
    ],
  },
  {
    version: '7.5.14',
    date: '2026-05-26',
    title: 'Top-Down framework live · JournifiSync rebuilt · journifiai.com launched',
    highlights: [
      {
        severity: 'feature',
        text: 'Top-Down chart-analysis framework — fully wired, not just foundation this time. Every trade now carries a 5-timeframe state vector (Daily / 4H / 1H / 15M / 5M) capturing Wyckoff cycle stage, EMA stack + slopes, Brooks "always-in" direction, Grimes trend integrity, AlphaRSI state, and volume regime. The MTF synthesis layer fuses bias (D + 4H) + zone (1H + 15M) + trigger (5M) into an A+ → F coherence grade, where F maps directly to the highest-risk entry shape v7.5.13 flagged in the direction-cohort audit. Five new Coach observations fire per trade: topdown-aligned (D/4H bias matches your direction), topdown-counter-bias (it doesn\'t — Wave F whisper), topdown-zone-approach (entry at a key 1H/15M zone), topdown-alwaysin-state (Brooks always-in vs your trade), topdown-stage-context (Wyckoff stage explanation). Plus your custom 9/21 EMA Cross (Diaz-style) joins the 7 existing pattern detectors as the 8th, firing on both Entry 1 (5-EMA pullback after cross) and Entry 2 (cross-price retest). Volume Profile chips render on trade cards when entries hit POC / VAH / VAL / HVN / LVN. AVWAP confluence zones (Shannon\'s multi-AVWAP convergence detection) and 4H body-close S/R clustering also shipped.',
      },
      {
        severity: 'fix',
        text: 'Coach no longer fires panic observations on cohorts that contain LIVE trades — anywhere. The first half of this fix landed earlier (13 composite / setup / ATM analyzers stopped treating mark-to-market unrealized losses as realized outcomes — the "1st Touch + Above All EMAs was everywhere but only 0% won" bug Chris caught during a live session). The second half extends the same gate to all six day-scope analyzers (dayGrade, dayPerformance, dayDrawdown, dayBaselineCompare, dayNewsEventImpact, dayEventCitations) — the "Rough day · review before the next session" toast no longer fires while you have a live trade open. Two-layer enforcement: upstream `summarizePnl` reports closed-only counts (new `pending` + `closedTotal` fields surface the live count), and `buildDayContext` derives the day\'s core stats from closed trades only. The contract going forward: any Coach surface that classifies outcomes by P&L must filter `isOpen` first.',
      },
      {
        severity: 'feature',
        text: 'New Account Type filter on the Journal header — pill chips for Sim / Eval / Funded / Live next to the existing Account dropdown. Toggle one or more to slice your P&L by category; empty = show all. Color-coded per type so the eye learns the palette (amber Sim, blue Eval, emerald Funded, purple Live). The classifier honors your existing `account.type` field (Paper / Evaluation / Funded / Live already used 26 / 26 of your accounts) with a name-override that catches edge cases like a "Sim101" account that got tagged as Evaluation by accident. Selection persists in settings.json — combos and toggles survive reloads.',
      },
      {
        severity: 'polish',
        text: 'JournifiSync modal rebuilt from the ground up — matches the modern frosted-glass / rounded-2xl style across the rest of the app, widened to a 2-col layout on bigger screens (Settings + Activity side by side). Status hero at the top color-codes the live state (sync active / paused / NT disconnected / no folder) with a primary Start/Pause button — no more reading three separate cards to figure out if sync is working. Three new functional touches: Include / Exclude filter mode toggle so the "blocker list" mental model actually works the way you think, search box for both the Account Filter and Account Mapping rows (essential with 22+ NT accounts), and a read-only diagnostic showing which accounts are blocked at the NT AddOn level (catches the "wait why isn\'t Sim101 syncing" mystery in one glance).',
      },
      {
        severity: 'fix',
        text: 'Trades that should have been closed but stayed "In Position" forever — now actually close. Root cause was the same one behind every historical Sim101 trade in your journal with quantity inflated to 16 / 24 / 32 contracts (the "Sim101 trades never close" issue you\'ve had since day 1): the main process re-emits today\'s full executions JSON on every sync restart, and the renderer was appending duplicate ExecutionIds to its per-key pending queue. That inflated `trade.quantity` past the exit count, which kept the close check (`totalExitQty >= totalEntryQty`) permanently false, which kept the trade flagged open. New `executionDedup.js` module filters incoming execs against ExecutionIds already in journal trades (handles the watcher re-emit case) plus dedups the per-key accumulator as a belt-and-suspenders second layer. Partial-fill trades close correctly when the position actually goes flat. Existing stuck trades in your journal stay as-is — edit / delete them by hand; future trades behave.',
      },
      {
        severity: 'feature',
        text: 'Live Coach panel now shows a "warming up" surface at early session instead of looking dead — used to be empty for the first ~2.5 hours of every futures session because of a 30-bar gate that blocked all detector output. Now five tiers explain what\'s happening: Waiting for bars → Session just started (1-2 bars in) → Setup engine warming up (3-9 bars, Signal Bar + 3-Bar Play eligible) → Almost fully armed (10-49 bars, 6 of 7 detectors live) → All setups armed — quiet bar (50+ bars). Each tier surfaces an ETA to the next detector unlock. Plus the bar-count gate was lowered from 30 → 0 so short-lookback detectors actually fire early — long-lookback ones still safely return null until enough bars print.',
      },
      {
        severity: 'fix',
        text: 'JournifiSync Account Filter selection now persists across reloads. Was a plain `useState([])` with no hydration — your selection evaporated every time you closed the modal or restarted the app. Saved to localStorage under `journifi.journifiSync.selectedAccounts`. Combined with the Include / Exclude mode toggle above, the "blocker list" approach finally sticks the way you expected — exclude Sim101 once, it stays excluded.',
      },
      {
        severity: 'polish',
        text: 'Desktop reliability — JournifiAI should no longer "go to sleep" when minimized or running in the background. Three changes: an Electron `powerSaveBlocker` signals the OS "don\'t suspend me" (the technical fix for Windows Modern Standby), a tray icon now persists when the window is closed so JournifiSync + the live-trade heartbeat + Live Coach keep running silently in the background, and a single-instance lock prevents duplicate launches. Dev mode is exempt from the tray + single-instance lock so `npm run electron:dev` can coexist with an installed production build resident in the tray.',
      },
      {
        severity: 'feature',
        text: '🌐 Journifi AI is now live on the web at **journifiai.com**. Same codebase, runs in the browser via localStorage fallback. The journal UI works (Dashboard, Trades, Setups, ATM, Calendar — all tabs), but NinjaTrader sync isn\'t wired in web mode yet (needs an architectural shift — desktop bridge → cloud API, planned for the next major release), and there\'s no auth or multi-device sync yet either (each browser is an isolated journal for now). Use the web build to check stats from another machine, share a snapshot view, or just see the project on a real public URL. Auth + cloud storage + the NT bridge are next-up after AlphaWolf Academy ships.',
      },
      {
        severity: 'breaking',
        text: 'Run Settings → Storage → Backfill bar analysis → "Refresh latest fields" to populate the new fields on your historical trades: Top-Down state vector (multiTf), MTF synthesis grade, Volume Profile (vp.poc / vah / val / hvn / lvn), AVWAP confluence zones, 4H body-close S/R, 9/21 EMA Cross detector results, Initial Balance breakout / reentry flags, plus the earlier-v7.5.14 fields (allTimeHigh, previousAth, overnightHigh / overnightLow). New trades pick them all up automatically.',
      },
    ],
  },
  {
    version: '7.5.13',
    date: '2026-05-21',
    title: 'Direction-aware Coach + trade card polish',
    highlights: [
      {
        severity: 'fix',
        text: 'Coach\'s live whispers now filter past-trade history by direction. Before: a LONG trade on "Support/Resistance" could be primed with "Watch 15m broke entry-bar-high" — a signal that\'s only adverse for SHORTS. The historical cohort was mixing direction-specific signatures into a single warning. Five analyzers got the same direction-match guard: counter-signal priming, setup-history WR, hour-window WR, duration profile (winner/loser medians), and EMA proximity intelligence (pullback edges + chase penalties on the Setups tab). For direction-flexible setups like S/R, observations now segregate cleanly per direction with explicit "(LONG)" / "(SHORT)" tags. For setups you only ever take one direction, titles stay clean. The detection layer was always correct; this fixes the aggregation layer that fed the priming whispers.',
      },
      {
        severity: 'fix',
        text: 'Coach no longer fires "X confluence was everywhere but only Y% won" on confluences that are IMPLIED by your setup. If your "15M 05 EMA" setup auto-applies the "15M 05 EMA" chip to every trade, that chip\'s universality is tautological — saying it "failed today" is just restating the setup outcome with the wrong framing. The filter also applies to the inverse winners-only signal. Setup-level Coach observations carry the actual signal in both cases.',
      },
      {
        severity: 'polish',
        text: 'Trade cards: Setup is now its own emerald pill with a "SETUP" label prefix (was plain text next to the ATM pill — too easy to confuse with a confluence chip). Implied-confluence chips that duplicate the setup name no longer render twice. Auto-synced metadata ("Auto-synced from NinjaTrader / ATM: X / Commission: $") collapses to a small ⓘ icon with the full text on hover, freeing up vertical space on the card stack. Your own notes still render normally.',
      },
      {
        severity: 'feature',
        text: 'Group quick-add (wand button on a session) now has a "Counter (✗)" tab alongside Confluences / Tags / Mistakes. Same category-grouped accordion UX, orange theming, ✗ prefix on chips. Toggling a chip as Counter on the group strips it from the positive Confluence array on each trade — same mutex the Edit Trade Modal enforces per-trade. Defaults to the Confluences tab so Counter is opt-in (the "collapsed unless you open it" pattern you asked for).',
      },
      {
        severity: 'fix',
        text: 'Edit Trade Modal\'s "Copy Confluences" preview now hides confluences implied by the source trade\'s setup. The apply handler already filtered them — but the preview chips were showing them anyway, suggesting they\'d propagate to the target trade when they wouldn\'t. Adds a small italic explainer ("Skipping N implied confluences from \'X\' — those auto-apply per setup, not per trade") when filtering trims anything.',
      },
    ],
  },
  {
    version: '7.5.12',
    date: '2026-05-20',
    title: 'Directional fix on pattern guidance + Settings polish',
    highlights: [
      {
        severity: 'fix',
        text: 'The "↳ Watch for:" line on Setups Firing Now cards now flips correctly for SHORT setups. Wolf Stack SHORT reads "Continuation candle that holds BELOW the stacked EMAs" (was "above" — backwards). Holy Grail SHORT reads "Rejection of the 20 EMA" (was "Reclaim"). 3-Bar Play SHORT references the 3-bar low. Important: the pattern detection itself was always correct — only the post-fire guidance text was direction-blind. Patterns whose follow-through is inherently directional now expose explicit long/short overrides in the catalog so this can\'t happen again.',
      },
      {
        severity: 'polish',
        text: 'Settings → General restyled to match the rest of the app — glass-card backgrounds (translucent + backdrop blur + subtle border) instead of the old solid gray panels. Manage Accounts and Scan for Duplicates buttons toned down to the same glass aesthetic. Equity Curve / Currency / Impact Level chip selectors now use the Slicer-style purple-on-glass treatment. The bulk-replace also caught the same card pattern in other Settings tabs (Setups, ATMs, etc.) as a free bonus.',
      },
      {
        severity: 'feature',
        text: 'Behind-the-scenes validation: re-ran the pattern detectors + follow-through stats math against live 5m bar data and confirmed the live cards are showing exactly what the pure analyzers produce. Small differences between two screenshots taken minutes apart reflect real bars closing in the interim — not data drift. The system does what it says; the directional fix above was the only inversion in the surface text.',
      },
    ],
  },
  {
    version: '7.5.11',
    date: '2026-05-20',
    title: 'ATM Archive — keep your active list clean',
    highlights: [
      {
        severity: 'feature',
        text: 'When you remove a template from NinjaTrader, the journal now auto-archives it on the next sync — keeps your active ATM list focused on what you actually trade today, without losing the historical trades and stats. Archived templates get a dashed amber border + "Archived · 12d ago" badge and live under a collapsible "▸ Archived (N)" section in the Library.',
      },
      {
        severity: 'feature',
        text: 'Analysis ranking now hides archived templates by default. Toolbar at the top shows "N archived hidden" + "Show archived" toggle + new "Refresh from NT" button (re-scan the NT folder without restarting the app). Each ranking card also has an inline Archive button — one click and the template drops out of the active ranking.',
      },
      {
        severity: 'feature',
        text: 'Manual Archive/Unarchive buttons on every ATM card (both Library and Analysis views). Use cases: a test template you keep in NT but don\'t actively trade, OR an orphaned template that had trades before the journal ever synced it. The manual flag overrides auto-sync — once you archive, it stays archived even if NT still has the file.',
      },
      {
        severity: 'polish',
        text: 'Edit Trade Modal\'s ATM picker dropdown now shows "(archived)" in italic amber next to retired templates. Still selectable (for historical trades), just visually flagged so you don\'t pick it for new trades by accident.',
      },
      {
        severity: 'fix',
        text: 'Empty-folder safety: if NinjaTrader isn\'t running (sync returns zero templates), the journal skips the archive comparison entirely instead of mass-archiving your whole library. Plus auto-unarchive — if a template reappears in NT, it goes back to active automatically.',
      },
    ],
  },
  {
    version: '7.5.10',
    date: '2026-05-20',
    title: 'Layout polish for narrow-column workflows',
    highlights: [
      {
        severity: 'polish',
        text: 'More usable horizontal width on every page — outer container padding trimmed (px-6 → px-2) and inner card padding tightened (p-6 → px-3 py-6). Nets ~56px of extra content width without removing the visual border. Ultra-wide users unaffected (max-w-[2400px] still centers).',
      },
      {
        severity: 'polish',
        text: 'Scrollbars now match the theme — subtle translucent thumb on a near-invisible track in both dark and light modes (was the default bright gray that looked out of place inside dark mode). Applies to the main page scroll AND every internal scroll container (Live Coach panel, modals, dropdowns).',
      },
      {
        severity: 'fix',
        text: '"Through Discipline Comes Freedom" tagline now lays out responsively as a true 3-column grid (controls left, tagline page-centered, empty spacer right). Wide widths give true page-centering; narrow widths gracefully wrap the tagline to its own row underneath. Always visible, never colliding with the Active Only / All Time controls, always actually centered.',
      },
    ],
  },
  {
    version: '7.5.8',
    date: '2026-05-19',
    title: 'Live Coach Feed gets superpowers',
    highlights: [
      {
        severity: 'feature',
        text: 'Multi-symbol watchlist in the AI Coach panel — pin up to 2 tickers and watch them side-by-side, each with independent polling and dismiss state. Use case: scalp MNQ while watching MES for confluence.',
        action: 'Click the "+ add" chip at the top of the AI Coach panel. Symbols come from your trade history.',
      },
      {
        severity: 'feature',
        text: 'Multi-TF alignment pills at the top of every watched ticker — Green = bullish (EMA stack up + price above shortest), Red = bearish, Yellow = mixed, Gray = unknown. Default 15M/1H/4H, customizable.',
        action: 'Click any pill to swap that slot\'s timeframe (1m through 1d available).',
      },
      {
        severity: 'feature',
        text: 'Setups Firing Now patterns now show their detection timeframe (5m badge) + a one-line "↳ Watch for:" guidance hint per pattern. No more guessing what to look for next after a fire.',
      },
      {
        severity: 'polish',
        text: 'Backfill is 5-8× faster (parallelized in chunks of 8) and runs silently in the background 2 min after each closed trade. The "Needs Backfill" pill should now stay at 0 during normal trading instead of incrementing on every fill.',
      },
      {
        severity: 'fix',
        text: 'Pile of backfill correctness fixes: pill finally clears (was double-counting analysis-failed + no-bars trades), primary "Run backfill" button now matches what the pill counts, explicit "· saved" / "SAVE FAILED" status, computeMaeMfe errors log instead of being silently swallowed, new "Force retry skipped" button to re-attempt marked trades after you drop new bar CSVs in.',
      },
      {
        severity: 'feature',
        text: 'Futures contract differentiation: new trade.contractMonth field captures M6/U6/H26 alongside the root symbol. All 12 NT month codes supported now (was only HMUZ — monthly futures like CL silently broke before). +16 new instruments: VX, VXM, BZ, MNG, ETH, MSL, MAV, CC, KC, CT, SB, OJ, RM, 5YY, SR3, SR1 (91 total).',
      },
      {
        severity: 'feature',
        text: 'ATM Edit Suggestions (read-only preview) — Coach now flags structured ATM template edits on the ATM tab. "Bump PT1 120t → 168t — 47 trades show MFE extending 40% past trigger." Phase 1 of a planned inline ATM editor (Phase 2 = save-as-new-template, Phase 3 = direct edit with auto-backup).',
      },
    ],
  },
  {
    version: '7.5.2',
    date: '2026-05-19',
    title: 'Live-trade polish + NT addon data integrity',
    highlights: [
      {
        severity: 'feature',
        text: 'One-tap Recommended Confluences in the live in-progress trade card.',
        action: 'Pin them in Settings → Setups → Edit Setup → Recommended Confluences.',
      },
      {
        severity: 'breaking',
        text: 'JournifiSync NT v3.1 — subscribes to 1m bars only, self-heals gaps every 5 min, exposes new "Last update" + sweep stats on the Bars dashboard.',
        action: 'Tools → Edit NinjaScript → AddOn → JournifiSyncNT → F5 to recompile. Higher timeframes (5m/15m/1h/4h) now derive from 1m on the journal side automatically.',
      },
      {
        severity: 'polish',
        text: 'Success toasts now render emerald + check icon. Were yellow + warning icon before, which made every "saved" message read like a warning.',
      },
      {
        severity: 'feature',
        text: 'This card you\'re reading. New What\'s New summary on every release so you don\'t have to read the CHANGELOG.',
      },
    ],
  },
  {
    version: '7.5.1',
    date: '2026-05-19',
    title: 'AVWAP Pack, Golden Crayon, Implied Confluence Auto-Tag',
    highlights: [
      {
        severity: 'feature',
        text: 'Five anchored VWAP levels computed at every trade entry — prior-day high/low/close + session high/low. New "Tagged AVWAP PDH/PDL/PDC/HoD/LoD" chips fire when entry is within 0.1% of any anchor.',
      },
      {
        severity: 'feature',
        text: 'Golden Crayon ((PDH+PDL)/2) is now a first-class level. Plus prior-day OHLC, today\'s open, intraday session high/low. New "Tagged Golden Crayon" chip in green when entry hits it.',
      },
      {
        severity: 'feature',
        text: 'Implied Confluences — pin chips on a setup that should auto-apply to every trade with that setup. Closes the "do I really have to tag the chip my setup is named after" friction.',
        action: 'Settings → Setups → Edit Setup → Implied Confluences. Use Settings → Storage → "Apply to historical trades" to backfill old trades.',
      },
      {
        severity: 'fix',
        text: 'Stale-chart fix — the file resolver now prefers the freshest candidate when multiple files could contain the same day\'s bars. No more reading 14-min-old data while JournifiSync NT is writing live.',
      },
      {
        severity: 'breaking',
        text: 'Run Settings → Storage → Backfill bar analysis → "Refresh latest fields" to populate AVWAPs + Golden Crayon on historical trades. New trades pick them up automatically.',
      },
    ],
  },
  {
    version: '7.4.5',
    date: '2026-05-18',
    title: 'Setup Pattern Library, Counter-Bias Whisper, Sequential Probability Engine',
    highlights: [
      {
        severity: 'feature',
        text: 'Seven mechanical pattern detectors from the educator canon: Wolf Stack (Diaz), Signal Bar + H2/L2 + Holy Grail + Turtle Soup (Brooks/Raschke), 3-Bar Play + Uno Reverse (Rake). Trade cards show a green chip for each pattern that fired.',
      },
      {
        severity: 'feature',
        text: 'Counter-Bias Whisper card in the Coach panel — when your journal thesis says "long" but a textbook SHORT setup fires on the latest 5m bar, the Coach quietly surfaces it. Not judgment, just awareness.',
      },
      {
        severity: 'feature',
        text: 'Live "Setups Firing Now" card — every actionable pattern firing on the latest 5m bar with today\'s follow-through probabilities color-coded by confidence + sample size.',
      },
    ],
  },
];

// ── Version comparison ────────────────────────────────────────────

/**
 * Compare two semver-ish strings. Returns a negative number if a < b,
 * 0 if equal, positive if a > b. Handles missing minor/patch as 0.
 * Examples:
 *   compareVersions('7.5.2', '7.5.1') → 1
 *   compareVersions('7.5.2', '7.5.10') → -8 (per-segment integer compare)
 *   compareVersions('7.5', '7.5.0') → 0
 */
export const compareVersions = (a, b) => {
  const parse = (v) => String(v || '0').split('.').map(n => parseInt(n, 10) || 0);
  const aa = parse(a);
  const bb = parse(b);
  const len = Math.max(aa.length, bb.length);
  for (let i = 0; i < len; i++) {
    const diff = (aa[i] || 0) - (bb[i] || 0);
    if (diff !== 0) return diff;
  }
  return 0;
};

/**
 * Latest version known to this build. Derived from WHATS_NEW[0] so the
 * version is single-source-of-truth in the data file — no risk of the
 * data and the trigger logic drifting apart across releases.
 */
export const CURRENT_VERSION = WHATS_NEW[0]?.version || '0.0.0';

/**
 * Return all WHATS_NEW entries newer than `seenVersion`. Empty array
 * when seenVersion is at or above current. Multi-release jumps return
 * every interim entry so a long-absent user sees what they missed.
 */
export const getUnseenEntries = (seenVersion) => {
  if (!seenVersion) return WHATS_NEW.slice(0); // null = treat all as unseen (handled by caller for fresh installs)
  return WHATS_NEW.filter(e => compareVersions(e.version, seenVersion) > 0);
};

// ── Severity → color tokens (consumed by the card) ───────────────

export const SEVERITY_COLOR = Object.freeze({
  feature:  { dot: 'bg-purple-400',  label: 'New'      },
  fix:      { dot: 'bg-blue-400',    label: 'Fix'      },
  breaking: { dot: 'bg-amber-400',   label: 'Action'   },
  polish:   { dot: 'bg-emerald-400', label: 'Polish'   },
});

// ── localStorage key (exported so callers can read/write consistently) ──

export const WHATS_NEW_SEEN_KEY = 'journifi.whatsNewSeen';
